import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { FileText, Send, Download, Trash2, Eye, RefreshCw, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractsManager() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts`);
      setContracts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (contractId) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/resend`);
      toast.success('Contract email resent');
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend');
    }
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) return;
    
    try {
      await axios.delete(`${API}/contracts/${contractId}`);
      toast.success('Contract deleted');
      fetchContracts();
    } catch (error) {
      toast.error('Failed to delete contract');
    }
  };

  const handleDownloadPdf = async (contractId, renterName) => {
    try {
      const response = await axios.get(`${API}/contracts/${contractId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract_${renterName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const viewContract = async (contractId) => {
    try {
      const response = await axios.get(`${API}/contracts/${contractId}`);
      setSelectedContract(response.data);
      setShowDetails(true);
    } catch (error) {
      toast.error('Failed to load contract details');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'signed':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" /> Signed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" /> Expired</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/Toronto'
      });
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Toronto'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Contracts</h1>
          <p className="text-gray-500">Manage rental contracts</p>
        </div>
        <Button variant="outline" onClick={fetchContracts}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{contracts.length}</div>
            <p className="text-sm text-gray-500">Total Contracts</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">
              {contracts.filter(c => c.status === 'signed').length}
            </div>
            <p className="text-sm text-gray-500">Signed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-yellow-600">
              {contracts.filter(c => c.status === 'pending').length}
            </div>
            <p className="text-sm text-gray-500">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">
              {contracts.filter(c => c.status === 'expired').length}
            </div>
            <p className="text-sm text-gray-500">Expired</p>
          </CardContent>
        </Card>
      </div>

      {/* Contracts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            All Contracts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {contracts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No contracts yet</p>
              <p className="text-sm">Send a contract from the Renters page</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Renter</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Signed</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contracts.map((contract) => (
                  <TableRow key={contract.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{contract.renter_name}</p>
                        <p className="text-sm text-gray-500">{contract.renter_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{contract.check_in_date}</p>
                        <p className="text-gray-500">to {contract.check_out_date}</p>
                      </div>
                    </TableCell>
                    <TableCell>${contract.total_amount?.toLocaleString()}</TableCell>
                    <TableCell>{getStatusBadge(contract.status)}</TableCell>
                    <TableCell>{formatDate(contract.sent_at)}</TableCell>
                    <TableCell>{formatDate(contract.signed_at)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => viewContract(contract.id)} title="View Details">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDownloadPdf(contract.id, contract.renter_name)} title="Download PDF">
                          <Download className="h-4 w-4" />
                        </Button>
                        {contract.status === 'pending' && (
                          <Button variant="ghost" size="icon" onClick={() => handleResend(contract.id)} title="Resend Email">
                            <Send className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(contract.id)} title="Delete">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Contract Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contract Details</DialogTitle>
            <DialogDescription>
              {selectedContract?.renter_name} - {selectedContract?.check_in_date}
            </DialogDescription>
          </DialogHeader>
          
          {selectedContract && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Status:</span>
                {getStatusBadge(selectedContract.status)}
              </div>

              {/* Renter Info */}
              <div>
                <h3 className="font-medium mb-2">Renter Information</h3>
                <div className="bg-gray-50 rounded-lg p-4 text-sm grid grid-cols-2 gap-2">
                  <p><span className="text-gray-500">Name:</span> {selectedContract.renter_name}</p>
                  <p><span className="text-gray-500">Email:</span> {selectedContract.renter_email}</p>
                  <p><span className="text-gray-500">Phone:</span> {selectedContract.renter_phone}</p>
                  <p><span className="text-gray-500">Address:</span> {selectedContract.renter_address}</p>
                </div>
              </div>

              {/* Rental Details */}
              <div>
                <h3 className="font-medium mb-2">Rental Details</h3>
                <div className="bg-gray-50 rounded-lg p-4 text-sm grid grid-cols-2 gap-2">
                  <p><span className="text-gray-500">Check-in:</span> {selectedContract.check_in_date} at {selectedContract.check_in_time}</p>
                  <p><span className="text-gray-500">Check-out:</span> {selectedContract.check_out_date} at {selectedContract.check_out_time}</p>
                  <p><span className="text-gray-500">Nights:</span> {selectedContract.num_nights}</p>
                  <p><span className="text-gray-500">Total:</span> ${selectedContract.total_amount?.toLocaleString()}</p>
                </div>
              </div>

              {/* Occupants (if signed) */}
              {selectedContract.status === 'signed' && selectedContract.occupants?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">Occupants</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {selectedContract.occupants.map((occ, idx) => (
                        <p key={idx}>{idx + 1}. {occ.name} (Age: {occ.age})</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Credit Card (if signed) */}
              {selectedContract.status === 'signed' && selectedContract.credit_card && (
                <div>
                  <h3 className="font-medium mb-2">Credit Card on File</h3>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm">
                    <p><span className="text-gray-500">Card:</span> {selectedContract.credit_card.card_type?.toUpperCase()} **** {selectedContract.credit_card.last4}</p>
                    <p><span className="text-gray-500">Cardholder:</span> {selectedContract.credit_card.cardholder_name}</p>
                    <p><span className="text-gray-500">Expires:</span> {selectedContract.credit_card.expiry_date}</p>
                  </div>
                </div>
              )}

              {/* Signature (if signed) */}
              {selectedContract.status === 'signed' && selectedContract.signature_data && (
                <div>
                  <h3 className="font-medium mb-2">Signature</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <img 
                      src={selectedContract.signature_data} 
                      alt="Signature" 
                      className="max-h-24 border rounded"
                    />
                    <p className="text-sm text-gray-500 mt-2">Signed on: {formatDate(selectedContract.signed_at)}</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button onClick={() => handleDownloadPdf(selectedContract.id, selectedContract.renter_name)}>
                  <Download className="h-4 w-4 mr-2" /> Download PDF
                </Button>
                {selectedContract.status === 'pending' && (
                  <Button variant="outline" onClick={() => handleResend(selectedContract.id)}>
                    <Send className="h-4 w-4 mr-2" /> Resend Email
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
